import { Result, ScoredTag, Tag } from "../components/Data.ts";
import { errorData, errorParser, fetchJSON } from "../components/Loaders.ts";
import { useUIStore } from "../components/Store.ts";
import { AddToast } from "../components/Toasts.tsx";
import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect } from "react";

export type ScoredWithTag = ScoredTag & {
  tag: Tag;
};

export interface DuplicateTag {
  id: number;
  first_tag: ScoredWithTag;
  second_tag: ScoredWithTag;
}

const loadDuplicateTags = (sessionId: string) => {
  const internal = async (): Promise<DuplicateTag[]> =>
    await fetchJSON<DuplicateTag[]>({
      sessionId,
      url: "/api/possible_duplicate_tags",
    })
      .then((result) => result.data)
      .catch((error) => Promise.reject(errorParser("duplicate tags", error)));
  return internal;
};

const discardPossibleDuplicate = async ({
  queryClient,
  pdId,
  queryKey,
}: {
  queryClient: QueryClient;
  pdId: number;
  queryKey: string[];
}) =>
  await fetchJSON<Result>({
    url: `/api/possible_duplicate/${pdId}/mark_inactive`,
    method: "PUT",
  })
    .then((result) => {
      console.debug("Marked as inactive", result.data);
      return queryClient
        .invalidateQueries({ queryKey })
        .then(() => Promise.resolve(result.data));
    })
    .catch((error) => {
      const data = errorData(error);
      return Promise.reject(data);
    });

const markAsMainTag = async ({
  queryClient,
  pdId,
  mainTagId,
  queryKey,
}: {
  queryClient: QueryClient;
  pdId: number;
  mainTagId: string;
  queryKey: string[];
}) =>
  await fetchJSON<Result>({
    url: `/api/possible_duplicate/${pdId}/mark_duplicate`,
    method: "PUT",
    content: { main_tag: mainTagId },
  })
    .then((result) => {
      console.debug("Marked as main", result.data);
      return queryClient
        .invalidateQueries({ queryKey })
        .then(() => Promise.resolve(result.data));
    })
    .catch((error) => {
      const data = errorData(error);
      return Promise.reject(data);
    });

function TagShow({ scoredTag }: { scoredTag: ScoredWithTag }) {
  const urlName = scoredTag.tag.name.replaceAll(
    new RegExp("[^A-Za-z]", "g"),
    "",
  );
  return (
    <Link to={`/Tag/${scoredTag.id}-${urlName}?name=${scoredTag.display}`}>
      {scoredTag.display}
    </Link>
  );
}

export function DuplicateRow({
  queryClient,
  duplicate,
  queryKey,
}: {
  queryClient: QueryClient;
  duplicate: DuplicateTag;
  queryKey: string[];
}) {
  return (
    <tr id={"pd-" + duplicate.id} data-testid={"pd-" + duplicate.id}>
      <td data-testid={"pd-show-first+" + duplicate.first_tag.id}>
        <TagShow scoredTag={duplicate.first_tag} />
      </td>
      <td data-testid={"button-" + duplicate.first_tag.id}>
        <button
          id={"first-primary-" + duplicate.id}
          type="submit"
          className="btn btn-primary"
          data-testid={"first-primary-" + duplicate.id}
          onClick={(e) => {
            markAsMainTag({
              queryClient,
              pdId: duplicate.id,
              mainTagId: duplicate.first_tag.tag.id,
              queryKey,
            })
              .then((res) => {
                AddToast(res["message"]);
              })
              .catch((res) => {
                AddToast((res as Result)["message"]);
              });
            e.preventDefault();
          }}
        >
          First is main
        </button>
      </td>
      <td data-testid={"pd-show-second-" + duplicate.second_tag.id}>
        <TagShow scoredTag={duplicate.second_tag} />
      </td>
      <td data-testid={"button-" + duplicate.second_tag.id}>
        <button
          id={"second-primary-" + duplicate.id}
          type="submit"
          className="btn btn-primary"
          data-testid={"second-primary-" + duplicate.id}
          onClick={(e) => {
            markAsMainTag({
              queryClient,
              pdId: duplicate.id,
              mainTagId: duplicate.second_tag.tag.id,
              queryKey,
            })
              .then((res) => {
                AddToast(res["message"]);
              })
              .catch((res) => {
                if (res !== null && "message" in res) {
                  AddToast((res as Result)["message"]);
                }
              });
            e.preventDefault();
          }}
        >
          Second is main
        </button>
      </td>
      <td>
        <button
          id={`discard-${duplicate.id}`}
          type="submit"
          className="btn btn-danger"
          data-testid={`discard-${duplicate.id}`}
          onClick={(e) => {
            discardPossibleDuplicate({
              queryClient,
              pdId: duplicate.id,
              queryKey,
            }).catch((res) => {
              if (res !== null && "message" in res) {
                AddToast((res as Result)["message"]);
              }
            });
            e.preventDefault();
          }}
        >
          Discard
        </button>
      </td>
    </tr>
  );
}

export function Duplicates() {
  const queryClient = useQueryClient();
  const sessionId = useUIStore((s) => s.sessionId);
  const LOAD_DUPLICATES_KEY = ["duplicate_tags"];
  const duplicates = useQuery({
    queryKey: LOAD_DUPLICATES_KEY,
    queryFn: loadDuplicateTags(sessionId),
  });

  useEffect(() => {
    document.title = "Freshet: Duplicate Tags";
  }, []);

  if (!duplicates.isSuccess) {
    return <div>Loading duplicates info...</div>;
  }
  return (
    <div id="duplicates-tab" data-testid="duplicates-tab">
      <table className="table">
        <thead>
          <tr>
            <th>First</th>
            <th></th>
            <th>Second</th>
            <th></th>
            <th>Discard</th>
          </tr>
        </thead>
        <tbody>
          {duplicates.data.map((duplicate) => (
            <DuplicateRow
              key={duplicate.id}
              queryClient={queryClient}
              duplicate={duplicate}
              queryKey={LOAD_DUPLICATES_KEY}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
